import { Stack, Typography } from '@mui/material';
import { Theme } from '@mui/material/styles';
import { useMemo } from 'react';
import { FormProvider } from 'react-hook-form';
import { Trans } from 'react-i18next';

import { FormErrorLogger } from '@/components/Form/FormErrorLogger.tsx';
import { LoadingButton } from '@/components/Form/LoadingButton';
import { Icon } from '@/components/Icon/Icon';
import { Divider } from '@/components/Layout/Divider';
import { Drawer, DrawerProps } from '@/components/Layout/Drawer';

import { COUNTRIES_WITH_POPULAR } from '@/constants/countries';

import { useAppSelector } from '@/hooks/redux';
import { useTranslate } from '@/hooks/useTranslate';

import { CountryOfResidenceField } from '@/pages/onboarding/address-information/components/AddressForm/CountryOfResidenceField';
import { MailingAddressSection } from '@/pages/onboarding/address-information/components/AddressForm/MailingAddressSection';

import { selectLoggedInUser } from '@/redux/auth/selectors';
import { selectConfigsState } from '@/redux/configs';
import { selectOperationData } from '@/redux/operation';

import { RequiredDocumentSection } from '@/types/document';

import { RequiredDocumentSelectDrawer } from '../RequiredDocumentSelectDrawer';
import {
  getAdultAddressData,
  getMailingFormDefaults,
  getMinorAddressData,
  getMinorInitialCountry,
  getNormalizedTaxResidences,
} from './helper';
import { MinorAddressFormFields } from './MinorAddressFormFields';
import { MinorLivesWithFields } from './MinorLivesWithFields';
import { MinorResidenceSelect } from './MinorResidenceSelect';
import { MinorShareAddressToggle } from './MinorShareAddressToggle';
import { MinorTaxResidencySection } from './MinorTaxResidencySection';
import { useMinorAddressForm } from './useMinorAddressForm';

import {
  AddressHeader,
  FormContainer,
  MinorPoADrawerContainer,
  ShareAddressAlert,
} from '../MinorProofOfAddressDrawer.styled';

type MinorProofOfAddressDrawerProps = {
  section: RequiredDocumentSection;
} & DrawerProps;

export function MinorProofOfAddressDrawer({
  section,
  onClose,
  ...props
}: MinorProofOfAddressDrawerProps) {
  const { t, country: translatedCountry } = useTranslate();
  const adultUser = useAppSelector(selectLoggedInUser);
  const { isoCode: localISO } = useAppSelector(selectConfigsState);
  const { documentByDocType, user: minorUser } =
    useAppSelector(selectOperationData);

  const adultMailingDefaults = useMemo(
    () => getMailingFormDefaults(adultUser),
    [adultUser],
  );

  const minorMailingDefaults = useMemo(
    () => getMailingFormDefaults(minorUser),
    [minorUser],
  );

  const normalizedTaxResidences = useMemo(
    () => getNormalizedTaxResidences(minorUser?.taxResidences),
    [minorUser?.taxResidences],
  );

  const {
    formMethods,
    isValid,
    isSubmitting,
    shouldAddMailingAddress,
    showDocumentUpload,
    handleFormSubmit,
    handleDocumentUploadClose,
  } = useMinorAddressForm({
    open: props.open ?? false,
    section,
    onClose,
  });

  const { setValue, watch } = formMethods;

  const popularCountryOptions = useMemo(
    () =>
      COUNTRIES_WITH_POPULAR.map(item => ({
        ...item,
        text: translatedCountry[item.value],
      })),
    [translatedCountry],
  );

  if (showDocumentUpload && section) {
    return (
      <RequiredDocumentSelectDrawer
        {...props}
        documentByDocType={documentByDocType}
        section={section}
        clientId={minorUser?.id}
        onClose={handleDocumentUploadClose}
      />
    );
  }

  const handleToggleChange = (isLiveWithMe: boolean) => {
    const adultAddress = getAdultAddressData(adultUser);
    const minorAddress = getMinorAddressData(minorUser);
    const addressToUse = isLiveWithMe ? adultAddress : minorAddress;
    const country = isLiveWithMe
      ? adultAddress.country
      : getMinorInitialCountry({
          adultUser,
          minorUser,
          minorAddress,
          localISO,
        });

    setValue('line1', addressToUse.line1);
    setValue('city', addressToUse.city);
    setValue('state', addressToUse.state);
    setValue('zip', addressToUse.zip);
    setValue('country', country);

    const differentTaxResidency = !isLiveWithMe;
    setValue('differentTaxResidency', differentTaxResidency);

    if (differentTaxResidency) {
      setValue('taxResidences', normalizedTaxResidences);
    }

    if (isLiveWithMe) {
      setValue('childLivesWithPersonName', '');
      setValue('childLivesWithRelationship', '');
      setValue('childLivesWithRelationshipDescription', '');
    } else {
      setValue(
        'childLivesWithPersonName',
        minorAddress.childLivesWithPersonName,
      );
      setValue(
        'childLivesWithRelationship',
        minorAddress.childLivesWithRelationship,
      );
      setValue(
        'childLivesWithRelationshipDescription',
        minorAddress.childLivesWithRelationshipDescription,
      );
    }

    if (shouldAddMailingAddress) {
      applyMailingDefaults(
        isLiveWithMe ? adultMailingDefaults : minorMailingDefaults,
      );
    }
  };

  const applyMailingDefaults = (
    mailingDefaults: typeof minorMailingDefaults,
  ) => {
    setValue(
      'mailingSameAsResidential',
      mailingDefaults.mailingSameAsResidential ?? true,
    );
    setValue('mailingLine1', mailingDefaults.mailingLine1 ?? '');
    setValue('mailingCity', mailingDefaults.mailingCity ?? '');
    setValue('mailingState', mailingDefaults.mailingState ?? '');
    setValue('mailingZip', mailingDefaults.mailingZip ?? '');
    setValue('mailingCountry', mailingDefaults.mailingCountry ?? '');
  };

  const watchShareAddress = watch('shareAddress');

  return (
    <Drawer
      {...props}
      onClose={onClose}
      title={t('documentSection.Proof_of_Address.title')}
      bgColor={(theme: Theme) => theme.palette.background.paper}
      headerBgColor={(theme: Theme) => theme.palette.background.paper}
    >
      <MinorPoADrawerContainer>
        <FormProvider {...formMethods}>
          <FormContainer component="form" onSubmit={handleFormSubmit}>
            <AddressHeader variant="h3">{t('minorPoA.header')}</AddressHeader>

            <MinorShareAddressToggle onLiveWithMeChange={handleToggleChange} />

            <ShareAddressAlert
              icon={<Icon name="info-outline" />}
              severity="info"
            >
              <Trans
                i18nKey="minorPoA.shareAddress.alert"
                components={{
                  br: <br />,
                  b: <Typography variant="captionBold" component="span" />,
                }}
              />
            </ShareAddressAlert>

            {!watchShareAddress && <MinorLivesWithFields />}

            <MinorAddressFormFields disabled={watchShareAddress} />

            <MinorResidenceSelect disabled={isSubmitting} />

            {shouldAddMailingAddress && (
              <Stack direction="column" width="100%" mt={1}>
                <MailingAddressSection
                  isDisabled={isSubmitting || watchShareAddress}
                  popularCountryOptions={popularCountryOptions}
                  isMinor
                />
                <Divider containerProps={{ my: 2 }} width="50%" />
                <CountryOfResidenceField
                  isDisabled={isSubmitting}
                  popularCountryOptions={popularCountryOptions}
                  helperText={t('minorPoA.countryOfResidence.helperText')}
                />
              </Stack>
            )}

            <MinorTaxResidencySection isSubmitting={isSubmitting} />

            <LoadingButton
              type="submit"
              variant="contained"
              fullWidth
              disabled={!isValid || isSubmitting}
              isLoading={isSubmitting}
            >
              {t('createNewAccount.addressPage.form.button.submit')}
            </LoadingButton>
          </FormContainer>
          <FormErrorLogger
            formName="MinorProofOfAddressDrawer"
            control={formMethods.control}
          />
        </FormProvider>
      </MinorPoADrawerContainer>
    </Drawer>
  );
}
